import { useMutation } from '@tanstack/react-query';
import { useTimeZone } from '@/auth/useTimeZone';
import { StartOrJoinSession } from '@/components/session/start-or-join-session';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { getSessionFormattedSchedule, getTeachingCoachId } from '@airreading/common';
import { DayOfWeekLabelMap, SortedDaysOfWeek, StudentSession } from '@airreading/common/data';
import { attendSession } from '@/lib/api-calls';
import unionIcon from '@/assets/icons/union-icon.svg';
import teacherIcon from '@/assets/icons/teacher-icon.svg';

interface StudentSessionCardProps {
  studentSession: StudentSession;
  studentName: string;
  isFuture?: boolean;
  index: number;
}

export const StudentSessionCard = ({ studentSession, studentName, isFuture, index }: StudentSessionCardProps) => {
  const { timeZone } = useTimeZone();
  const { attendeeUrl, startInstant, coach, status, substitutionRequests } = studentSession;

  const { mutateAsync: attendSessionMutate } = useMutation({
    mutationFn: attendSession,
  });

  const coachIdWhoIsTeaching = getTeachingCoachId({
    coachId: coach?.id || null,
    status,
    substitutionRequests,
  });
  const isCurrentCoachTeaching = coach?.id === coachIdWhoIsTeaching;

  const substituteCoach = substitutionRequests.find(
    (req) => req.substituteId === coachIdWhoIsTeaching,
  )?.substituteCoach;

  const previousCoachDisplayName = coach?.coachName ?? '-';
  const currentCoachDisplayName = isCurrentCoachTeaching ? previousCoachDisplayName : substituteCoach?.coachName ?? '-';

  const plainDate = startInstant.toZonedDateTimeISO(timeZone).toPlainDate();
  const startDayOfWeek = SortedDaysOfWeek[plainDate.dayOfWeek - 1];
  const { sessionTimeFormatted } = getSessionFormattedSchedule({
    ...studentSession,
    displayTimeZone: timeZone,
  });

  const zonedDateTime = startInstant.toZonedDateTimeISO(timeZone);
  const date = zonedDateTime.toPlainDate();

  const monthDayFormatted = `${date.toLocaleString('en-US', { month: 'short' })} ${date.day}`;

  const onStartOrJoinSessionButtonClick = () => attendSessionMutate({ id: studentSession.sessionId });

  const cardTestId = `student-session-card-${index + 1}`;
  const dateTestId = `${cardTestId}-date`;
  const timeTestId = `${cardTestId}-time`;
  const studentTestId = `${cardTestId}-student`;
  const teacherTestId = `${cardTestId}-teacher`;
  const joinButtonTestId = `${cardTestId}-button-join`;

  return (
    <Card className="w-[300px]">
      <CardHeader className={isFuture ? 'px-6 pt-6 pb-3' : 'p-6'}>
        <CardTitle>
          <div data-testid={dateTestId}>
            {DayOfWeekLabelMap[startDayOfWeek]?.abbr}, {monthDayFormatted}
          </div>
        </CardTitle>
        <div>
          <div className="my-2 text-lg font-semibold lowercase" data-testid={timeTestId}>
            {sessionTimeFormatted}
          </div>
          <div className={`mt-2 ${isFuture ? 'mb-4' : ''}`}>
            <div className="flex items-center">
              <img src={unionIcon} alt="Union Icon" className="w-5 h-5" />
              <span
                className=" my-2 font-normal text-base leading-6 tracking-[-0.75%] text-center align-middle capitalize ml-2 text-gray-600"
                data-testid={studentTestId}
              >
                {studentName}
              </span>
            </div>
            <div className="flex items-center">
              <img src={teacherIcon} alt="teacher Icon" className="w-5 h-5" />
              <span
                className="my-2 font-normal text-base leading-6 tracking-[-0.75%] text-center align-middle capitalize ml-2 text-gray-600"
                data-testid={teacherTestId}
              >
                {currentCoachDisplayName}
              </span>
            </div>
          </div>
          {!isFuture && (
            <StartOrJoinSession
              label="Join class"
              url={attendeeUrl}
              whenUrlEmptyRender={
                <Button disabled>
                  <div>No valid meeting link available</div>
                </Button>
              }
              startButtonProps={{
                onClick: onStartOrJoinSessionButtonClick,
                className: 'bg-[#eb404a] hover:bg-[#d23841] rounded-sm py-[16px] px-8',
                icon: undefined,
                'data-testid': joinButtonTestId,
              }}
            />
          )}
        </div>
      </CardHeader>
    </Card>
  );
};
